import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Share2, Trash2, X, Pencil, Bell, BellOff } from 'lucide-react';
import { SEL_API } from '../../lib/selectionShare';
import { useVipGuardRequest } from '../../lib/vipGuard';
import { useAuth } from '../../contexts/AuthContext';

// Goal / result alerts of this selection (on by default)
const NotifyToggle = ({ sel }) => {
  const [on, setOn] = useState(sel.notify !== false);
  useEffect(() => setOn(sel.notify !== false), [sel.notify]); // list reloaded (e.g. after "Sì" on the save question)
  const toggle = async () => {
    try {
      await axios.put(`${SEL_API}/${sel.selection_id}/notify`, { notify: !on });
      setOn(!on);
      sel.notify = !on; // keep the list item in sync: the card remounts when the preview is reopened
      toast.success(on ? 'Notifiche spente per questa selezione' : 'Notifiche accese: gol, esiti e riepilogo');
    } catch { toast.error('Non riuscito, riprova'); }
  };
  const Icon = on ? Bell : BellOff;
  return (
    <button type="button" onClick={toggle} className={`p-2 rounded-lg ${on ? 'text-[#F5A623]' : 'text-gray-500'}`} aria-label={on ? 'Spegni notifiche' : 'Accendi notifiche'} data-testid={`saved-selection-notify-${sel.selection_id}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
};

const eur = (n) => Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const scoreLabel = (st) => {
  if (!st || st.score_home == null) return null;
  const s = `${st.score_home}-${st.score_away}`;
  if (st.status === 'HT') return `${s} · INT`;
  return st.elapsed != null ? `${s} · ${st.elapsed}'` : s;
};

const fmtDay = (d) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : d || '');

// Bets of the same match shown together (a Sistema can add them in any order); states keep their own index
const byMatch = (picks) => {
  const first = {};
  picks.forEach((p, i) => { const k = p.fixture_id ?? `i${i}`; if (first[k] == null) first[k] = i; });
  return picks.map((p, i) => [p, i]).sort((a, b) => first[a[0].fixture_id ?? `i${a[1]}`] - first[b[0].fixture_id ?? `i${b[1]}`] || a[1] - b[1]);
};

export const SelectionRows = ({ picks, states, id }) => (
  <div>
    {byMatch(picks).map(([p, i]) => {
      const st = states?.[i] || (p.final ? { status: p.final.status, score_home: p.final.home, score_away: p.final.away, state: p.state } : p.state ? { state: p.state } : null);
      const won = st?.state === 'won';
      const score = scoreLabel(st);
      const live = st?.elapsed != null || st?.status === 'HT';
      return (
        <div key={`${p.fixture_id}-${i}`} className={`px-3 py-2.5 border-t border-white/5 flex items-center gap-3 ${won ? 'bg-[#22C55E]/15' : ''}`}
          style={won ? { boxShadow: 'inset 3px 0 0 #22C55E' } : undefined} data-testid={`sel-row-${id}-${i}`} data-state={st?.state || 'pending'}>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] text-gray-500 font-bold uppercase tracking-wider truncate">{fmtDay(p.match_date)} {p.match_time}{p.league_name ? ` · ${p.league_name}` : ''}</p>
            <p className="text-[13px] font-bold text-white truncate">{p.fixed && <span className="mr-1 px-1 rounded bg-[#F5A623] text-[#1A1A1A] text-[10px] font-black" data-testid={`sel-fixed-${id}-${i}`}>F</span>}{p.home_team} <span className="text-gray-500 font-normal">vs</span> {p.away_team}</p>
          </div>
          <div className="text-right shrink-0 max-w-[45%]">
            {st?.state === 'lost' && <X className="inline-block w-4 h-4 mr-1 text-red-500 align-middle" strokeWidth={3.5} data-testid={`sel-lost-${id}-${i}`} />}
            <span className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-black ${won ? 'bg-[#22C55E]/25 text-[#22C55E]' : 'bg-[#F5A623]/20 text-[#F5A623]'}`}>{p.market}</span>
            {st?.state === 'void' && <span className="ml-1.5 text-[10px] font-black text-gray-400" data-testid={`sel-void-${id}-${i}`}>ANNULLATA</span>}
            {st?.state === 'half_won' && <span className="ml-1.5 text-[10px] font-black text-[#22C55E]" data-testid={`sel-half-won-${id}-${i}`}>VINTA A METÀ</span>}
            {st?.state === 'half_lost' && <span className="ml-1.5 text-[10px] font-black text-red-400" data-testid={`sel-half-lost-${id}-${i}`}>PERSA A METÀ</span>}
            {st?.state === 'unverifiable' && ['FT', 'AET', 'PEN'].includes(st?.status) && <span className="ml-1.5 text-[10px] font-black text-gray-400" data-testid={`sel-unverified-${id}-${i}`}>DA VERIFICARE</span>}
            {score && (
              <p className={`mt-1 text-[11px] font-black flex items-center justify-end gap-1 ${won ? 'text-[#22C55E]' : 'text-white'}`} data-testid={`sel-score-${id}-${i}`}>
                {live && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}{score}
              </p>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

export const SelectionAmounts = ({ sel }) => (
  <div className="grid grid-cols-3 text-center bg-[#151515] py-2.5" data-testid={`sel-amounts-${sel.selection_id || sel.share_code}`}>
    <div><p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Importo</p><p className="text-sm font-black text-white">{eur(sel.stake)}</p></div>
    <div><p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Moltiplicatore</p><p className="text-sm font-black text-[#F5A623]">{sel.multiplier ? `${sel.multiplier}x` : '—'}</p></div>
    <div><p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Potenziale</p><p className="text-sm font-black text-white">{eur(sel.potential)}</p></div>
  </div>
);

// mounted while a "Selezione Vincenzo" copy is on screen → VIP screen protection on the Diario page
export const VipGuard = () => { useVipGuardRequest(true); return null; };

export const VipCopyBadge = ({ id }) => (
  <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-[#F5A623] text-[#1A1A1A] text-[9px] font-black uppercase tracking-wider" data-testid={`sel-vip-badge-${id}`}>Selezione Vincenzo</span>
);

export const SelectionCard = React.memo(({ sel, states, onShare, onDelete, onEdit, bare }) => {
  const isAdmin = !!useAuth().user?.is_admin;
  return (
  <div className={bare ? 'bg-[#1A1A1A]' : 'rounded-2xl overflow-hidden border-2 border-[#F5A623]/35 bg-[#1A1A1A] shadow-[0_6px_24px_rgba(0,0,0,0.45)]'} data-testid={`saved-selection-${sel.selection_id}`}>
    {sel.vip_copy && <VipGuard />}
    <div className="flex items-center justify-between px-3 py-2.5 bg-[#F5A623]/10 border-b border-[#F5A623]/25">
      <div className="min-w-0">
        <p className="text-xs font-black text-white truncate">{sel.source === 'link' ? `Di ${sel.owner_name}` : 'La mia selezione'}</p>
        {sel.vip_copy && !bare && <VipCopyBadge id={sel.selection_id} />}
        {sel.description ? <p className="text-[11px] font-black text-[#F5A623] truncate" data-testid={`sel-description-${sel.selection_id}`}>{sel.description}</p> : null}
        {sel.booking_code ? <p className="text-[10px] text-gray-500">Codice precompilata: <span className="text-gray-300 font-bold">{sel.booking_code}</span></p> : null}
      </div>
      <div className="flex items-center gap-1">
        {onDelete && sel.live && <NotifyToggle sel={sel} />}
        {onEdit && <button type="button" onClick={() => onEdit(sel)} className="p-2 rounded-lg text-gray-400 hover:text-[#F5A623]" aria-label="Modifica" data-testid={`saved-selection-edit-${sel.selection_id}`}><Pencil className="w-4 h-4" /></button>}
        {onShare && (!sel.vip_copy || isAdmin) && <button type="button" onClick={() => onShare(sel)} className="h-8 px-3 rounded-full bg-[#F5A623] text-[#1A1A1A] text-xs font-black flex items-center gap-1.5 active:scale-95 transition-transform" data-testid={`saved-selection-share-${sel.selection_id}`}><Share2 className="w-3.5 h-3.5" /> Condividi</button>}
        {onDelete && <button type="button" onClick={() => onDelete(sel)} className="p-2 rounded-lg text-gray-500 hover:text-red-400" data-testid={`saved-selection-delete-${sel.selection_id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
    <SelectionRows picks={sel.picks} states={states} id={sel.selection_id} />
    <SelectionAmounts sel={sel} />
    {sel.status === 'archived' && (
      <p className="px-3 py-2 text-[11px] text-gray-400 border-t border-white/5" data-testid={`sel-result-${sel.selection_id}`}>
        Esito: <span className={`font-black ${sel.result === 'won' ? 'text-[#22C55E]' : 'text-gray-300'}`}>{sel.result === 'won' ? `Vinta · ${eur(sel.won_amount)}` : sel.result === 'unverified' ? 'Da verificare' : sel.result === 'void' ? 'Annullata (importo restituito)' : 'Persa'}</span>
        {sel.void_legs ? ` · ${sel.void_legs} ${sel.void_legs === 1 ? 'evento annullato' : 'eventi annullati'}` : ''}
      </p>
    )}
  </div>
  );
});

export default SelectionCard;
